import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { Button, Box, TextField, InputAdornment, IconButton } from "@mui/material";
import AppInfoView from "@crema/core/AppInfoView";
import AppsContainer from "@crema/core/AppsContainer";
import AppsPagination from "@crema/core/AppsPagination";
import AppsContent from "@crema/core/AppsContainer/AppsContent";
import AppsHeader from "@crema/core/AppsContainer/AppsHeader";
import { AppState } from "redux/store";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { getTransactionRecords } from "redux/actions";
import TransactionTable from "./TransactionPageTable";

const Transaction = () => {
  const { messages } = useIntl();
  const dispatch = useDispatch();
  const { transaction } = useSelector<AppState, AppState["transactionMaster"]>(
    ({ transactionMaster }) => transactionMaster
  );

  const rowPerPage = 50;
  const [page, setPage] = useState(0);
  const [search, setSearchQuery] = useState<string>("");
  const [searchField, setSearchField] = useState("all");

  const searchFields = [
    { label: "All", value: "all" },
    { label: "Customer Name", value: "FirstName" },
  { label: "RMN", value: "ContactNumber" },
  { label: "LeadKycId", value: "Lead_EkycId" },
  { label: "CAF ID", value: "CafId" },
  { label: "Order Id", value: "order_id" },
  { label: "Txn Id", value: "TransactionId" },
  { label: "Amount", value: "payableAmount" },
  ];

  const exportTransactionToCSV = (transactionData: any[]) => {
    const csvRows: string[] = [];

    // Updated headers based on your latest list
    const headers = [
      "Id",
      "Type",
      "COF ID",
      "Customer Name",
      "RMN",
      "Order Id",
      "Payment Mode",
      "Txn Id",
      "Txn Date",
      "Amount",
      "Status",
    ];
    csvRows.push(headers.join(","));

    transactionData.forEach((item) => {
      const row = [
        item.id || "",
        item.Lead_EkycId || "",
        item.CafId || "",
        item.FirstName || "",
        item.ContactNumber || "",
        item.order_id || "",
        item.PaymentMode || "",
        item.TransactionId || "",
        item.updated_at || "",
        item.payableAmount || "",
        String(item.txn_status ?? "").toLowerCase() === "charged" ? "Success" : "Failed",
      ];
      csvRows.push(row.join(","));
    });

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "transaction.csv";
    link.click();
  };

  const onPageChange = (
    event: React.ChangeEvent<unknown> | null,
    value: number
  ) => {
    setPage(value);
  };

  useEffect(() => {
    const offset = page * rowPerPage;
    dispatch(getTransactionRecords(search, offset));
  }, [dispatch, search, page]);

  const onSearchTransaction = (value: string) => {
    setSearchQuery(value);
    setPage(0);
  };

  const filteredTransaction = search.trim()
    ? transaction.filter((item) => {
      const searchTerm = search.trim().toLowerCase();

      if (searchField === "all") {
        return searchFields
          .filter((f) => f.value !== "all")
          .some(({ value }) => {
            const fieldValue = item[value];
            return fieldValue?.toString().toLowerCase().includes(searchTerm);
          });
      } else {
        const fieldValue = item[searchField];
        return fieldValue?.toString().toLowerCase().includes(searchTerm);
      }
    })
    : transaction;

  const paginatedTransactions = filteredTransaction.slice(
    page * rowPerPage,
    (page + 1) * rowPerPage
  );

  return (
    <>
      <AppsContainer
        title={
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: 16,
                fontWeight: "bold",
                color: "text.primary",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {messages["sidebar.manageTransaction"]}
            </Box>

            <Button
              variant="contained"
              color="secondary"
              onClick={() => exportTransactionToCSV(transaction)}
            >
              Export
            </Button>
          </Box>
        }
        fullView
      >
        <AppsHeader>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              justifyContent: "flex-end",
              width: 1,
            }}
          >
            <TextField
              select
              size="small"
              value={searchField}
              onChange={(e) => {
                setSearchField(e.target.value);
                setSearchQuery("");
                setPage(0);
              }}
              SelectProps={{ native: true }}
            >
              {searchFields.map((field) => (
                <option key={field.value} value={field.value}>
                  {field.label}
                </option>
              ))}
            </TextField>

            <TextField
              variant="outlined"
              size="small"
              value={search}
              onChange={(e) => onSearchTransaction(e.target.value)}
              placeholder={messages["common.searchHere"] as string}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {search ? (
                      <IconButton
                        onClick={() => onSearchTransaction("")}
                        size="small"
                      >
                        <CloseIcon />
                      </IconButton>
                    ) : (
                      <SearchIcon />
                    )}
                  </InputAdornment>
                ),
              }}
              sx={{ width: 200 }}
            />
          </Box>
        </AppsHeader>

        <AppsContent
          sx={{
            paddingTop: 2.5,
            paddingBottom: 2.5,
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
          }}
        >
          <TransactionTable transaction={paginatedTransactions} />
          <AppsPagination
            rowsPerPage={rowPerPage}
            count={filteredTransaction.length}
            page={page}
            onPageChange={onPageChange}
          />
        </AppsContent>
      </AppsContainer>

      <AppInfoView />
    </>
  );
};

export default Transaction;
